/**
 * Tells overlays rendered by the scheduling-ui React 17 island apart from the
 * host's own.
 *
 * The island's remote bundles its own @mui/material, whose modals, menus,
 * poppers and date pickers portal straight into `document.body`, outside any
 * host drawer. The host can't see them through its own MUI ModalManager, so it
 * needs another way to know they belong to the island.
 *
 * Ownership comes from which React renderer created the node. Every React DOM
 * copy tags the elements it creates with `__reactFiber$<key>` and the root
 * containers it renders into with `__reactContainer$<key>`, where `<key>` is a
 * random string per React copy. The island registers the host div it renders
 * its React 17 root into; that div's `__reactContainer$` key is React 17's, so
 * a body-level node carrying `__reactFiber$` with the same key was created by
 * the island's React 17 tree (directly or through a portal). Host overlays
 * carry React 19's key and never match. Unlike guessing from timing or class
 * names, this can't mistake a host Popper or Tooltip for an island one.
 *
 * Client-only and free of React 17: AppDrawer imports it in the main bundle.
 */

const REACT_CONTAINER_PREFIX = '__reactContainer$';
const REACT_FIBER_PREFIX = '__reactFiber$';

// overlay roots MUI (and MUI X pickers) portal into document.body
const OVERLAY_ROOT_SELECTOR =
  '.MuiModal-root, .MuiPopper-root, .MuiPickersPopper-root';

// sentinels around a MUI FocusTrap; focusing them re-routes focus
const FOCUS_SENTINEL_SELECTOR =
  '[data-testid="sentinelStart"], [data-testid="sentinelEnd"]';

const TABBABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]'
].join(', ');

const islandContainers = new Set<Element>();

/**
 * Register the element a React 17 island root renders into.
 * Call it after the first `ReactDOM17.render` into the element.
 * @returns a function that unregisters it
 */
export function registerIslandContainer(container: Element): () => void {
  islandContainers.add(container);
  return () => {
    islandContainers.delete(container);
  };
}

function islandFiberKeys(): string[] {
  const keys: string[] = [];
  islandContainers.forEach((container) => {
    Object.keys(container).forEach((key) => {
      if (key.startsWith(REACT_CONTAINER_PREFIX)) {
        keys.push(
          REACT_FIBER_PREFIX + key.slice(REACT_CONTAINER_PREFIX.length)
        );
      }
    });
  });
  return keys;
}

/** Whether a React 17 island tree created this DOM node */
export function isIslandOwnedNode(node: Element): boolean {
  return islandFiberKeys().some((key) =>
    Object.prototype.hasOwnProperty.call(node, key)
  );
}

function isOpenOverlayRoot(node: Element): boolean {
  if (!node.matches(OVERLAY_ROOT_SELECTOR)) {
    return false;
  }
  // tooltips open on focus/hover of their trigger; they never take focus
  if (
    node.classList.contains('MuiTooltip-popper') ||
    node.getAttribute('role') === 'tooltip'
  ) {
    return false;
  }
  // closed keepMounted modals and poppers stay in the DOM, hidden
  if (
    node.classList.contains('MuiModal-hidden') ||
    (node as HTMLElement).style?.display === 'none'
  ) {
    return false;
  }
  return true;
}

/**
 * Island-owned overlay roots open in `document.body`, outside `exclude`,
 * in DOM order (the last one is on top).
 */
export function getOpenIslandOverlays(exclude?: Element | null): Element[] {
  if (islandContainers.size === 0 || typeof window === 'undefined') {
    return [];
  }
  return Array.from(window.document.body.children).filter(
    (node) =>
      !(exclude && exclude.contains(node)) &&
      isOpenOverlayRoot(node) &&
      isIslandOwnedNode(node)
  );
}

/**
 * Move focus into an overlay: its first tabbable element (skipping MUI focus
 * sentinels), else its focus-trap container (`tabindex="-1"`), else the
 * overlay itself if it can take focus.
 * @returns whether focus moved into the overlay
 */
export function focusIntoOverlay(overlay: Element): boolean {
  const candidates = Array.from(
    overlay.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)
  ).filter((el) => !el.matches(FOCUS_SENTINEL_SELECTOR));
  const target =
    candidates.find((el) => el.tabIndex >= 0) ??
    candidates.find((el) => el.getAttribute('tabindex') === '-1') ??
    (overlay.hasAttribute('tabindex') ? (overlay as HTMLElement) : null);
  if (!target) {
    return false;
  }
  target.focus();
  return overlay.contains(overlay.ownerDocument.activeElement);
}

/**
 * Test-only: forget registered containers.
 * @internal
 */
export function __resetIslandContainersForTest__() {
  islandContainers.clear();
}
